// src/components/Auth/SignUp.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  CircularProgress,
  Grid,
  LinearProgress,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google,
  Facebook,
  Apple,
  Check,
  Clear,
} from '@mui/icons-material';
import { signUp, confirmSignUp, resendSignUpCode, signInWithRedirect } from 'aws-amplify/auth';
import { useApp } from '../../contexts/AppContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { HypeCircleLogo } from '../Branding';
import { 
  validateEmail, 
  validatePassword, 
  validateDisplayName, 
  validatePhoneNumber,
  calculatePasswordStrength,
  getPasswordStrengthColor,
  getPasswordStrengthText 
} from '../../utils/authValidation';
import { useAuthSecurity } from '../../hooks/useAuthSecurity';
import SecurityAlert from './SecurityAlert';
import EmailVerification from './EmailVerification';

const SignUp = ({ onToggleAuthMode }) => {
  const { addNotification } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    securityState, 
    checkSubmissionLimit, 
    logSecurityEvent 
  } = useAuthSecurity();
  
  // Get return URL from location state
  const returnUrl = location.state?.from?.pathname || '/movies';
  const [step, setStep] = useState('signup'); // 'signup' or 'confirm'
  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    agreeToTerms: false,
  });
  const [confirmationCode, setConfirmationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: [] });
  const [emailAvailable, setEmailAvailable] = useState(null);

  const lastCheckedEmail = useRef('');



  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'phoneNumber':
        error = validatePhoneNumber(value);
        break;
      case 'displayName':
        error = validateDisplayName(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'confirmPassword':
        if (!value) {
          error = 'Please confirm your password';
        } else if (value !== formData.password) {
          error = 'Passwords do not match';
        }
        break;
      case 'agreeToTerms':
        if (!value) {
          error = 'You must agree to the terms and conditions';
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Clear field-specific errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear API errors when user modifies form
    if (errors.api) {
      setErrors(prev => ({ ...prev, api: '' }));
    }

    // Calculate password strength
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  // Check email availability
  useEffect(() => {
    if (!formData.email || errors.email || formData.email === lastCheckedEmail.current) {
      return;
    }

    const checkEmail = async () => {
      if (!/^\S+@\S+\.\S+$/.test(formData.email)) return;

      lastCheckedEmail.current = formData.email;
      
      try {
        // Simulate API call - replace with actual API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // For demo purposes, assume email is available if it doesn't contain 'test'
        const available = !formData.email.toLowerCase().includes('test');
        
        if (lastCheckedEmail.current === formData.email) {
          setEmailAvailable(available);
          if (!available) {
            setErrors(prev => ({ ...prev, email: 'Email is already registered' }));
          }
        }
      } catch (error) {
        console.error('Error checking email availability:', error);
      }
    };

    const timeout = setTimeout(checkEmail, 800);
    return () => clearTimeout(timeout);
  }, [formData.email, errors.email]);

  const handleSignUp = async (e) => {
    e.preventDefault();

    // Validate all fields
    const validations = [
      validateField('email', formData.email),
      validateField('phoneNumber', formData.phoneNumber),
      validateField('displayName', formData.displayName),
      validateField('password', formData.password),
      validateField('confirmPassword', formData.confirmPassword),
      validateField('agreeToTerms', formData.agreeToTerms),
    ];

    if (!validations.every(Boolean) || emailAvailable === false) {
      return;
    }

    setLoading(true);
    setErrors({});
    setEmailAvailable(null); // Reset email availability check

    try {
      const { user, nextStep } = await signUp({
        username: formData.email.trim(),
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email.trim(),
            phone_number: formData.phoneNumber.trim() ? `+91${formData.phoneNumber.trim()}` : '+910000000000',
            name: formData.displayName.trim(),
          },
          autoSignIn: false, // Disable auto sign-in to handle confirmation properly
        },
      });

      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setStep('confirm');
        addNotification({
          type: 'success',
          message: 'Account created! Please check your email for the confirmation code.',
        });
      } else if (nextStep.signUpStep === 'DONE') {
        addNotification({
          type: 'success',
          message: 'Account created successfully! Please sign in to continue.',
        });
        setTimeout(() => onToggleAuthMode(), 1500);
      }
    } catch (error) {
      console.error('Sign up error:', error);
      
      let errorMessage = 'Sign up failed. Please try again.';
      
      switch (error.name) {
        case 'UsernameExistsException':
          // Check if user needs to confirm email first
          try {
            // Try to resend confirmation code to see if user is unconfirmed
            await resendSignUpCode({ username: formData.email.trim() });
            
            // If successful, user exists but is unconfirmed
            setStep('confirm');
            addNotification({
              type: 'info',
              message: 'Account exists but not confirmed. Please check your email for the confirmation code.',
              duration: 8000
            });
            return; // Exit the catch block
          } catch (resendError) {
            // User exists and is confirmed, or other error
            addNotification({
              type: 'warning',
              message: 'Account already exists. Please sign in instead.',
              duration: 6000,
              action: {
                label: 'Sign In',
                onClick: () => onToggleAuthMode()
              }
            });
            
            // Don't set form errors for this case, just show notification
            return; // Exit without setting form errors
          }
          break;
        case 'InvalidParameterException':
          errorMessage = 'Please check your information and try again.';
          setErrors({ api: errorMessage });
          break;
        case 'InvalidPasswordException':
          errorMessage = 'Password does not meet requirements.';
          setErrors({ password: errorMessage });
          break;
        case 'TooManyRequestsException':
          errorMessage = 'Too many attempts. Please wait a few minutes before trying again.';
          setErrors({ api: errorMessage });
          break;
        default:
          errorMessage = error.message || 'Sign up failed. Please try again.';
          setErrors({ api: errorMessage });
      }

      addNotification({
        type: 'error',
        message: errorMessage,
        duration: 6000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();

    if (!confirmationCode.trim()) {
      setErrors({ confirmationCode: 'Confirmation code is required' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const { nextStep } = await confirmSignUp({
        username: formData.email.trim(),
        confirmationCode: confirmationCode.trim(),
      });

      if (nextStep.signUpStep === 'DONE') {
        addNotification({
          type: 'success',
          message: 'Email confirmed successfully! Please sign in to continue.',
        });
        // Switch to sign in instead of auto-redirecting
        setTimeout(() => onToggleAuthMode(), 1500);
      }
    } catch (error) {
      console.error('Confirmation error:', error);
      
      let errorMessage = 'Confirmation failed. Please try again.';
      
      switch (error.name) {
        case 'CodeMismatchException':
          errorMessage = 'Invalid confirmation code. Please check and try again.';
          break;
        case 'NotAuthorizedException':
          if (error.message.includes('Current status is CONFIRMED') || error.message.includes('User cannot be confirmed')) {
            addNotification({
              type: 'success',
              message: 'Your account is already confirmed! You can now sign in.',
            });
            setTimeout(() => onToggleAuthMode(), 1500);
            return;
          }
          errorMessage = 'Confirmation failed. Please try again.';
          break;
        case 'ExpiredCodeException':
          errorMessage = 'Confirmation code has expired. Please request a new one.';
          break;
        case 'AliasExistsException':
          errorMessage = 'An account with this email already exists. Please sign in instead.';
          setTimeout(() => onToggleAuthMode(), 2000);
          break;
        case 'TooManyRequestsException':
          errorMessage = 'Too many attempts. Please wait before trying again.';
          break;
        default:
          errorMessage = error.message || 'Confirmation failed. Please try again.';
      }

      setErrors({ confirmationCode: errorMessage });
      addNotification({
        type: 'error',
        message: errorMessage,
        duration: 6000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      await resendSignUpCode({
        username: formData.email.trim(), // Use email as username for resend code
      });
      
      addNotification({
        type: 'success',
        message: 'Confirmation code sent to your email.',
      });
    } catch (error) {
      console.error('Resend code error:', error);
      addNotification({
        type: 'error',
        message: 'Failed to resend confirmation code.',
      });
    } finally {
      setLoading(false);
    }
  };



  const isSignUpFormValid = React.useMemo(() => {
    const hasRequiredFields = 
      formData.email.trim() &&
      formData.displayName.trim() &&
      formData.password &&
      formData.confirmPassword &&
      formData.agreeToTerms;
    
    const hasFieldErrors = Object.entries(errors).some(([key, error]) => 
      key !== 'api' && error && error !== ''
    );
    
    const emailNotAvailable = emailAvailable === false;
    
    return hasRequiredFields && !hasFieldErrors && !emailNotAvailable && !loading;
  }, [
    formData.email,
    formData.displayName, 
    formData.password,
    formData.confirmPassword,
    formData.agreeToTerms,
    errors,
    emailAvailable,
    loading
  ]);

  if (step === 'confirm') {
    return (
      <EmailVerification 
        email={formData.email}
        onVerified={() => {
          logSecurityEvent('email_verified', { email: formData.email });
          onToggleAuthMode();
        }}
        onBack={() => setStep('signup')}
      />
    );
  }

  return (
    <Card sx={{ 
      maxWidth: { xs: '100%', sm: 450, md: 500, lg: 550 }, 
      width: '100%', 
      mx: 'auto', 
      borderRadius: { xs: 0, sm: 3 }, 
      boxShadow: { xs: 0, sm: 3 },
      minHeight: { xs: '100vh', sm: 'auto' }
    }}>
      <CardContent sx={{ 
        p: { xs: 3, sm: 4, md: 5 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: { xs: 'flex-start', sm: 'flex-start' },
        minHeight: { xs: '100vh', sm: 'auto' }
      }}>
        <Box textAlign="center" mb={{ xs: 4, sm: 3 }} mt={{ xs: 2, sm: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <HypeCircleLogo size="lg" variant="default" />
          </Box>
          <Typography 
            variant={{ xs: 'h5', sm: 'h4' }} 
            component="h1" 
            fontWeight="bold" 
            gutterBottom
            sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}
          >
            Create Account
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              fontSize: { xs: '0.95rem', sm: '1rem' },
              maxWidth: { xs: '100%', sm: '85%', md: '75%' },
              mx: 'auto'
            }}
          >
            🎬 Join thousands of movie lovers sharing their passion!
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            <Chip size="small" label="📝 Write Reviews" variant="outlined" />
            <Chip size="small" label="💬 Chat with Friends" variant="outlined" />
            <Chip size="small" label="📚 Track Watchlists" variant="outlined" />
            <Chip size="small" label="⭐ Rate Movies" variant="outlined" />
          </Box>
        </Box>

        {/* Security Alerts */}
        <SecurityAlert type="password-security" />
        
        {securityState.isRateLimited && (
          <SecurityAlert type="account-security" persistent={true} />
        )}

        {/* Error Alert */}
        {errors.api && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.api}
          </Alert>
        )}

        {/* Google Sign In Button */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Google />}
          onClick={async () => {
            try {
              setLoading(true);
              await signInWithRedirect({ provider: 'Google' });
            } catch (error) {
              console.error('Google sign in error:', error);
              addNotification({
                type: 'error',
                message: 'Failed to sign in with Google. Please try again.',
              });
              setLoading(false);
            }
          }}
          disabled={loading}
          sx={{ 
            mb: 2, 
            py: 1.5,
            borderColor: '#4285f4',
            color: '#4285f4',
            '&:hover': {
              borderColor: '#357ae8',
              backgroundColor: 'rgba(66, 133, 244, 0.04)'
            }
          }}
        >
          Continue with Google
        </Button>

        {/* Facebook Sign In Button */}
       {/*  <Button
          fullWidth
          variant="outlined"
          startIcon={<Facebook />}
          onClick={async () => {
            try {
              setLoading(true);
              await signInWithRedirect({ provider: 'Facebook' });
            } catch (error) {
              console.error('Facebook sign in error:', error);
              addNotification({
                type: 'error',
                message: 'Failed to sign in with Facebook. Please try again.',
              });
              setLoading(false);
            }
          }}
          disabled={loading}
          sx={{ 
            mb: 2, 
            py: 1.5,
            borderColor: '#1877f2',
            color: '#1877f2',
            '&:hover': {
              borderColor: '#166fe5',
              backgroundColor: 'rgba(24, 119, 242, 0.04)'
            }
          }}
        >
          Continue with Facebook
        </Button>

        <Divider sx={{ my: 2 }}>or</Divider> */}

        {/* Sign Up Form */}
        <Box component="form" onSubmit={handleSignUp} noValidate>
          <TextField
            fullWidth
            name="email"
            label="Email Address (Login ID)"
            type="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.email}
            helperText={errors.email || 'This will be your login username'}
            disabled={loading}
            autoComplete="email"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: formData.email && !errors.email && (
                <InputAdornment position="end">
                  {emailAvailable === null ? (
                    <CircularProgress size={16} />
                  ) : emailAvailable ? (
                    <Check color="success" />
                  ) : (
                    <Clear color="error" />
                  )}
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            name="phoneNumber"
            label="Phone Number (Optional)"
            type="tel"
            value={formData.phoneNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber || '10-digit Indian mobile number (optional)'}
            disabled={loading}
            autoComplete="tel"
            sx={{ mb: 2 }}
            placeholder="9876543210"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body1" color="text.secondary">
                    +91
                  </Typography>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            name="displayName"
            label="Display Name"
            type="text"
            value={formData.displayName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.displayName}
            helperText={errors.displayName || 'How others will see your name'}
            disabled={loading}
            autoComplete="name"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.password}
            helperText={errors.password}
            disabled={loading}
            autoComplete="new-password"
            sx={{ mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Password Strength Indicator */}
          {formData.password && (
            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" color="text.secondary">
                  Password Strength
                </Typography>
                <Chip 
                  label={getPasswordStrengthText(passwordStrength.score)} 
                  size="small" 
                  color={getPasswordStrengthColor(passwordStrength.score)}
                  variant="outlined"
                />
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={passwordStrength.score}
                color={getPasswordStrengthColor(passwordStrength.score)}
                sx={{ height: 4, borderRadius: 2 }}
              />
              {passwordStrength.feedback.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {passwordStrength.feedback.map((feedback, index) => (
                    <Typography 
                      key={index}
                      variant="caption" 
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: '0.75rem' }}
                    >
                      • {feedback}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            disabled={loading}
            autoComplete="new-password"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <Link href="/terms" target="_blank" sx={{ textDecoration: 'none' }}>
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link href="/privacy" target="_blank" sx={{ textDecoration: 'none' }}>
                  Privacy Policy
                </Link>
              </Typography>
            }
            sx={{ mb: 3, alignItems: 'flex-start' }}
          />

          {errors.agreeToTerms && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.agreeToTerms}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!isSignUpFormValid || loading}
            sx={{ 
              mb: 2, 
              py: { xs: 2, sm: 1.5 },
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontWeight: 600
            }}
          >
            {loading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} color="inherit" />
                Creating Account...
              </Box>
            ) : (
              'Create Account'
            )}
          </Button>
        </Box>

        {/* Anonymous User Redirect */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            🎬 Explore trending movies and shows without signing up
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/movies')}
            sx={{ 
              mb: 2,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.9rem'
            }}
          >
            Browse Movies & Shows
          </Button>
        </Box>

        {/* Sign In Link */}
        <Box textAlign="center" mt={{ xs: 3, sm: 2 }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.9rem', sm: '0.875rem' } }}
          >
            Already have an account?{' '}
            <Link
              component="button"
              variant="body2"
              onClick={onToggleAuthMode}
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                fontWeight: 'medium',
                fontSize: { xs: '0.9rem', sm: '0.875rem' },
              }}
            >
              Sign in here
            </Link>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SignUp;